import { prepareSavePointPayload } from "@/hooks/create-page/utils/formStateHelpers";
import { FormPersistenceService } from "@/services/formPersistenceService";
import type { CreatePageState } from "@/stores/createPageStore";
import {
  beginTrackedCreateAuthHandoff,
  cancelTrackedCreateAuthHandoff,
} from "@/utils/createAuthHandoffLifecycle";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import { sanitizeCreateReturnUrl } from "@/utils/creditCheckoutReturnUrl";
import SavePoint from "@/utils/savePoint";

export const CREATE_SIGN_IN_HANDOFF_ERROR =
  "Your dress is still here. Allow site data, then try again.";

export interface PreparedCreateSignInHandoff {
  intentId: string;
  returnPath: string;
}

/**
 * Prepares a generic signed-out/anonymous-to-account return from `/create`.
 *
 * Unlike credit and quote continuations, this handoff does not require a
 * generated image, ticket, pending action, or even an agent-session id. A
 * prompt/style/reference-only draft is still customer work and must survive
 * both existing-account password sign-in and Google OAuth.
 */
export function prepareCreateSignInHandoff({
  state,
  currentHref,
  sourceUserId,
}: {
  state: CreatePageState;
  currentHref: string;
  sourceUserId?: string | null;
}): PreparedCreateSignInHandoff | null {
  // A settled anonymous identity gets an owner-bound capability so the
  // identity boundary can distinguish this return from a forged URL. A truly
  // signed-out draft has no source Auth row to bind, but it still needs an
  // opaque, durable SavePoint key before Google can leave the tab.
  const handoffId = beginTrackedCreateAuthHandoff(sourceUserId, {
    allowUnowned: !sourceUserId,
  });
  if (!handoffId) return null;

  try {
    const returnUrl = sanitizeCreateReturnUrl(currentHref);
    if (
      returnUrl.pathname !== "/create" &&
      !returnUrl.pathname.startsWith("/create/")
    ) {
      cancelTrackedCreateAuthHandoff(handoffId);
      return null;
    }

    FormPersistenceService.saveState({
      data: prepareSavePointPayload(state, 1),
      source: "auth_redirect",
      intentId: handoffId,
    });

    // OAuth and email confirmation can finish in a new tab. Do not navigate
    // unless the exact URL-scoped snapshot is readable from durable storage.
    const durableSavePoint = SavePoint.getDurableSavePoint(handoffId);
    if (
      durableSavePoint?.version !== 1 ||
      durableSavePoint.intentId !== handoffId ||
      durableSavePoint.source !== "auth_redirect" ||
      !durableSavePoint.data
    ) {
      cancelTrackedCreateAuthHandoff(handoffId);
      return null;
    }

    returnUrl.searchParams.set(CREATE_AUTH_HANDOFF_PARAM, handoffId);
    // Carry the restore marker through either provider. The OAuth callback and
    // email form also add it defensively, but the identity boundary needs the
    // destination to be self-contained if those layers change independently.
    returnUrl.searchParams.set("restore_form", "true");

    return {
      intentId: handoffId,
      returnPath: returnUrl.pathname + returnUrl.search + returnUrl.hash,
    };
  } catch {
    cancelTrackedCreateAuthHandoff(handoffId);
    return null;
  }
}
